/**
 * Call slots offered before checkout. Four slots, at least 90 min ahead,
 * with different hours depending on the weekday. Stable for the browser session.
 */

export const CALL_WINDOW_KEY = 'openbro_call_window_v3';

/** Sunday first, matching Date#getDay. Three distinct hours per weekday. */
const HOURS_BY_WEEKDAY = [
  [11 * 60, 15 * 60 + 30, 19 * 60],
  [10 * 60, 12 * 60 + 30, 18 * 60 + 30],
  [9 * 60 + 30, 14 * 60, 19 * 60],
  [11 * 60 + 30, 16 * 60, 20 * 60],
  [10 * 60 + 30, 13 * 60 + 30, 18 * 60],
  [12 * 60 + 30, 16 * 60 + 30, 19 * 60 + 30],
  [10 * 60, 14 * 60 + 30, 17 * 60 + 30],
];

export function hoursForDay(day) {
  return HOURS_BY_WEEKDAY[day.getDay()];
}

function slotOnDay(day, minutes) {
  const slot = new Date(day);
  slot.setHours(0, 0, 0, 0);
  slot.setMinutes(minutes);
  return slot;
}

export function createCallWindow(now = new Date()) {
  const lead = now.getTime() + 90 * 60 * 1000;
  const slots = [];
  for (let offset = 0; offset < 5 && slots.length < 4; offset += 1) {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    hoursForDay(day).forEach((minutes) => {
      if (slots.length >= 4) return;
      const slot = slotOnDay(day, minutes);
      if (slot.getTime() < lead) return;
      slots.push(slot.toISOString());
    });
  }
  if (!slots.length) {
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    slots.push(slotOnDay(tomorrow, 12 * 60 + 30).toISOString());
  }
  return { slots, selected: slots[Math.floor(Math.random() * slots.length)] };
}

export function readCallWindow() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CALL_WINDOW_KEY) || 'null');
    const stillAhead = saved?.slots?.length && saved.slots.every((iso) => Date.parse(iso) > Date.now() + 90 * 60 * 1000);
    if (!stillAhead || !saved.slots.includes(saved.selected)) return null;
    return saved;
  } catch {
    return null;
  }
}

/** Saved window if still valid, otherwise a fresh one (and saves it). */
export function loadCallWindow() {
  const saved = readCallWindow();
  if (saved) return saved;
  const next = createCallWindow();
  sessionStorage.setItem(CALL_WINDOW_KEY, JSON.stringify(next));
  return next;
}

export function saveCallWindow(window) {
  sessionStorage.setItem(CALL_WINDOW_KEY, JSON.stringify(window));
}

/**
 * Libellés dans l'heure locale du visiteur. Anglais = usage US : « 3:30 PM », « Tue, Oct 6 », et le fuseau
 * (« EDT », « PDT ») dans le libellé complet quand le navigateur le donne en lettres (pas « GMT+2 »).
 */
export function formatCallSlot(iso, lang, now = new Date()) {
  const date = new Date(iso);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const time = lang === 'fr'
    ? `${hours}h${minutes}`
    : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dayStart = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const dayDelta = Math.round((dayStart(date) - dayStart(now)) / 86400000);
  const day = dayDelta === 0
    ? (lang === 'fr' ? "Aujourd'hui" : 'Today')
    : dayDelta === 1
      ? (lang === 'fr' ? 'Demain' : 'Tomorrow')
      : date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' });
  const weekday = date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });
  const chip = dayDelta === 0
    ? time
    : dayDelta === 1
      ? (lang === 'fr' ? `dem. ${time}` : `Tmrw ${time}`)
      : `${weekday} ${time}`;
  let zone = '';
  if (lang !== 'fr') {
    const part = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(date).find((p) => p.type === 'timeZoneName');
    if (part && /^[A-Z]{2,5}$/.test(part.value)) zone = ` ${part.value}`;
  }
  return { iso, time, day, chip, label: `${day} · ${time}${zone}` };
}
